// Shared HTML builders for the report PDF download so the export mirrors the
// on-screen view (PreviewReportModal) section by section, with a consistent
// enterprise document design used by both download entry points
// (PreviewReportModal and OperationsList).
import {sortFieldsByOrder} from './reportStructure'
import {
  formatConsumptionMetric,
  formatConsumptionStoredValue,
  isConsumptionMultiRowFieldLabel,
  normalizeScalarForDisplay,
} from './valueNormalization'
import { synthesizeTankWiseFuelRows } from '../components/fuelChangeoverReportSchema'

// ---------------------------------------------------------------------------
// Design tokens (inline styles only — html2canvas renders inline CSS reliably)
// ---------------------------------------------------------------------------
const NAVY = '#0f2a43'
const ACCENT = '#009ef7'
const INK = '#182433'
const MUTED = '#5e6e82'
const BORDER = '#d9e2ec'
const SECTION_BG = '#f1f5f9'
const ZEBRA_BG = '#fafbfd'

const FONT_STACK = "'Helvetica Neue', Helvetica, Arial, sans-serif"

const LABEL_STYLE = `font-size:8.5px; text-transform:uppercase; letter-spacing:0.6px; color:${MUTED}; margin-bottom:2px;`
const VALUE_STYLE = `font-size:11px; font-weight:600; color:${INK}; word-break:break-word;`

const TABLE_STYLE = `width:100%; border-collapse:collapse; font-size:10px; color:${INK};`
const TH_STYLE = `border:1px solid ${NAVY}; background:${NAVY}; color:#ffffff; padding:6px 8px; font-size:9px; text-transform:uppercase; letter-spacing:0.5px; text-align:left; font-weight:600;`
const TD_STYLE = `border:1px solid ${BORDER}; padding:6px 8px; text-align:left; vertical-align:top;`

const escapeHtml = (input: any): string => {
  const text = input === null || input === undefined || input === '' ? '--' : String(input)
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const capitalizeWords = (str: string) => str.replace(/\b\w/g, (char) => char.toUpperCase())

/** Clean label used by the view (keeps "Start Position Latitude" style labels tidy). */
const getCleanLabel = (label: string) => {
  const lower = String(label || '').toLowerCase()

  if (lower.includes('latitude')) {
    const prefix = lower.split('latitude')[0].trim()
    return `${prefix ? capitalizeWords(prefix) + ' ' : ''}Latitude`
  }

  if (lower.includes('longitude')) {
    const prefix = lower.split('longitude')[0].trim()
    return `${prefix ? capitalizeWords(prefix) + ' ' : ''}Longitude`
  }

  return capitalizeWords(String(label || ''))
}

const normalizeFieldLabel = (label: any) => String(label || '').trim().toLowerCase()

const hasMeaningfulValue = (value: any) => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim() !== ''
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value).length > 0
  return true
}

const parseJsonLike = (raw: any): any => {
  if (raw === null || raw === undefined || raw === '') return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }
  return raw
}

const parseJsonArray = (raw: any): any[] => {
  const parsed = parseJsonLike(raw)
  return Array.isArray(parsed) ? parsed : []
}

// ---------------------------------------------------------------------------
// Document chrome (header banner, meta strip, sections, footer)
// ---------------------------------------------------------------------------
export type ReportExportMeta = {
  title?: string
  vesselName?: string
  imoNumber?: any
  vesselType?: string
  voyageNumber?: any
  departurePort?: string
  arrivalPort?: string
  reportDate?: string
}

const metaCellHtml = (label: string, value: any) => `
  <td style="padding:8px 12px; border:1px solid ${BORDER}; width:33.33%; vertical-align:top;">
    <div style="${LABEL_STYLE}">${escapeHtml(label)}</div>
    <div style="${VALUE_STYLE}">${escapeHtml(value)}</div>
  </td>
`

/** Opens the export document: branded banner + vessel/voyage meta strip. */
export const renderReportDocumentStartHtml = (meta: ReportExportMeta): string => {
  const route =
    meta.departurePort || meta.arrivalPort
      ? `${meta.departurePort || '--'} → ${meta.arrivalPort || '--'}`
      : '--'

  return `
    <div style="font-family:${FONT_STACK}; font-size:11px; color:${INK};">
      <div style="background:${NAVY}; border-radius:6px 6px 0 0; padding:16px 20px; display:flex; justify-content:space-between; align-items:center;">
        <div>
          <div style="font-size:9px; text-transform:uppercase; letter-spacing:1.5px; color:#9fb6cc; margin-bottom:4px;">Vessel Operations Report</div>
          <div style="font-size:18px; font-weight:700; color:#ffffff;">${escapeHtml(meta.title)}</div>
        </div>
        <div style="text-align:right;">
          <div style="font-size:9px; text-transform:uppercase; letter-spacing:1px; color:#9fb6cc; margin-bottom:4px;">Report Date</div>
          <div style="font-size:11px; font-weight:600; color:#ffffff;">${escapeHtml(meta.reportDate)}</div>
        </div>
      </div>
      <div style="height:3px; background:${ACCENT}; margin-bottom:12px;"></div>
      <table style="width:100%; border-collapse:collapse; margin-bottom:16px; page-break-inside:avoid;">
        <tr>
          ${metaCellHtml('Vessel Name', meta.vesselName)}
          ${metaCellHtml('IMO Number', meta.imoNumber)}
          ${metaCellHtml('Vessel Type', meta.vesselType)}
        </tr>
        <tr>
          ${metaCellHtml('Voyage No.', meta.voyageNumber)}
          ${metaCellHtml('Route', route)}
          ${metaCellHtml('Report Date', meta.reportDate)}
        </tr>
      </table>
  `
}

/** Closes the export document with a generated-on footer. */
export const renderReportDocumentEndHtml = (meta?: ReportExportMeta): string => {
  const generatedOn = new Date().toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
  return `
      <div style="margin-top:20px; padding-top:8px; border-top:1px solid ${BORDER}; display:flex; justify-content:space-between; page-break-inside:avoid;">
        <div style="font-size:8.5px; color:${MUTED};">This document was generated electronically and is valid without signature.</div>
        <div style="font-size:8.5px; color:${MUTED};">Generated on ${escapeHtml(generatedOn)}${
    meta?.vesselName ? ` • ${escapeHtml(meta.vesselName)}` : ''
  }</div>
      </div>
    </div>
  `
}

/** Section card: accent header band + body, matching one on-screen accordion tab. */
export const renderSectionHtml = (title: string, bodyHtml: string): string => `
  <div style="border:1px solid ${BORDER}; border-radius:6px; margin-bottom:14px; page-break-inside:avoid; overflow:hidden;">
    <div style="background:${SECTION_BG}; border-left:4px solid ${ACCENT}; border-bottom:1px solid ${BORDER}; padding:8px 14px; page-break-after:avoid;">
      <span style="font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.8px; color:${NAVY};">${escapeHtml(
        title
      )}</span>
    </div>
    <div style="padding:12px 14px;">
      ${bodyHtml}
    </div>
  </div>
`

const mutedNoteHtml = (text: string) => `<div style="font-size:10px; color:${MUTED};">${escapeHtml(text)}</div>`

export const renderNoFieldsBodyHtml = (): string => mutedNoteHtml('No fields defined.')

// ---------------------------------------------------------------------------
// Field grid (generic tabs) — 4 label/value cells per row, like the view
// ---------------------------------------------------------------------------
type GridItem = {label: string; valueHtml: string}

const renderFieldGridHtml = (items: GridItem[]): string => {
  if (!items.length) return mutedNoteHtml('No data captured for this section.')

  const chunkSize = 4
  let out = `<table style="width:100%; border-collapse:collapse; table-layout:fixed;">`
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize)
    out += `<tr style="page-break-inside:avoid;">`
    for (let j = 0; j < chunkSize; j++) {
      const item = chunk[j]
      out += item
        ? `<td style="width:25%; padding:6px 10px 6px 0; border-bottom:1px solid ${SECTION_BG}; vertical-align:top;">
             <div style="${LABEL_STYLE}">${escapeHtml(item.label)}</div>
             <div style="${VALUE_STYLE}">${item.valueHtml}</div>
           </td>`
        : `<td style="width:25%; border-bottom:1px solid ${SECTION_BG};"></td>`
    }
    out += `</tr>`
  }
  out += `</table>`
  return out
}

// ---------------------------------------------------------------------------
// Bunker tables (styled to match the design system)
// ---------------------------------------------------------------------------
const asBool = (value: any) => value === true || String(value || '').trim().toLowerCase() === 'true'

const asBlockNumber = (value: any) => {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : 1
}

const parseBunkerAttachmentRows = (raw: any): any[] => {
  const parsed = parseJsonLike(raw)
  if (!parsed) return []
  if (Array.isArray(parsed)) return parsed
  if (typeof parsed === 'object') {
    const uploaded = Array.isArray((parsed as any).uploaded)
      ? (parsed as any).uploaded.map((item: any) => ({...item, source: 'uploaded'}))
      : []
    const pending = Array.isArray((parsed as any).pending)
      ? (parsed as any).pending.map((item: any) => ({...item, source: 'pending'}))
      : []
    return [...uploaded, ...pending]
  }
  return []
}

const subheadingHtml = (text: string) =>
  `<div style="font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.6px; color:${NAVY}; margin:12px 0 6px;">${escapeHtml(
    text
  )}</div>`

const renderBunkerRowsTableHtml = (raw: any): string => {
  const rows = parseJsonArray(raw)
  if (!rows.length) return ''

  const blocks = Array.from(new Set(rows.map((row) => asBlockNumber(row?.reportBlock)))).sort(
    (a, b) => a - b
  )

  let out = subheadingHtml('Bunker Received Rows')

  blocks.forEach((block, idx) => {
    const blockRows = rows.filter((row) => asBlockNumber(row?.reportBlock) === block)
    out += `<div style="font-size:10px; font-weight:600; color:${MUTED}; margin:6px 0 4px;">Report ${
      idx + 1
    }</div>`
    out += `
      <table style="${TABLE_STYLE} margin-bottom:8px;">
        <thead>
          <tr>
            <th style="${TH_STYLE} width:36px; text-align:center;">Row</th>
            <th style="${TH_STYLE}">Date (UTC)</th>
            <th style="${TH_STYLE}">Time (UTC)</th>
            <th style="${TH_STYLE}">Port</th>
            <th style="${TH_STYLE}">Fuel Type</th>
            <th style="${TH_STYLE}">Quantity (MT)</th>
            <th style="${TH_STYLE}">Storage</th>
            <th style="${TH_STYLE}">Storage Qty</th>
          </tr>
        </thead>
        <tbody>
    `
    blockRows.forEach((row, rowIndex) => {
      const allocationOnly = asBool(row?.allocationOnly)
      const zebra = rowIndex % 2 === 1 ? ` background:${ZEBRA_BG};` : ''
      out += `
        <tr style="page-break-inside:avoid;${zebra}">
          <td style="${TD_STYLE} text-align:center;">${rowIndex + 1}</td>
          <td style="${TD_STYLE}">${escapeHtml(allocationOnly ? '--' : row?.dateUtc)}</td>
          <td style="${TD_STYLE}">${escapeHtml(allocationOnly ? '--' : row?.timeUtc)}</td>
          <td style="${TD_STYLE}">${escapeHtml(allocationOnly ? '--' : row?.port)}</td>
          <td style="${TD_STYLE}">${escapeHtml(allocationOnly ? '--' : row?.fuelType)}</td>
          <td style="${TD_STYLE}">${escapeHtml(allocationOnly ? '--' : row?.quantity)}</td>
          <td style="${TD_STYLE}">${escapeHtml(row?.storage)}</td>
          <td style="${TD_STYLE}">${escapeHtml(row?.storageQuantity)}</td>
        </tr>
      `
    })
    out += '</tbody></table>'
  })

  return out
}

const renderBunkerAttachmentsTableHtml = (raw: any): string => {
  const rows = parseBunkerAttachmentRows(raw)
  if (!rows.length) return ''

  let out = subheadingHtml('Bunker Attachments')
  out += `
    <table style="${TABLE_STYLE}">
      <thead>
        <tr>
          <th style="${TH_STYLE} width:50px; text-align:center;">Report</th>
          <th style="${TH_STYLE}">Document</th>
          <th style="${TH_STYLE}">File</th>
          <th style="${TH_STYLE}">Remarks</th>
          <th style="${TH_STYLE}">Source</th>
        </tr>
      </thead>
      <tbody>
  `
  rows.forEach((row, rowIndex) => {
    const zebra = rowIndex % 2 === 1 ? ` background:${ZEBRA_BG};` : ''
    out += `
      <tr style="page-break-inside:avoid;${zebra}">
        <td style="${TD_STYLE} text-align:center;">${escapeHtml(asBlockNumber(row?.reportBlock))}</td>
        <td style="${TD_STYLE}">${escapeHtml(row?.documentName)}</td>
        <td style="${TD_STYLE}">${escapeHtml(row?.fileName)}</td>
        <td style="${TD_STYLE}">${escapeHtml(row?.remarks)}</td>
        <td style="${TD_STYLE}">${escapeHtml(row?.source || '--')}</td>
      </tr>
    `
  })
  out += '</tbody></table>'
  return out
}

// ---------------------------------------------------------------------------
// Generic tab body — mirrors the on-screen view field-by-field
// ---------------------------------------------------------------------------
const HIDDEN_BUNKER_SCALAR_LABELS = new Set([
  'year',
  'vessel',
  'date (utc)',
  'time (utc)',
  "hfo (mt's)",
  "mgo (mt's)",
])

const isDmsPart = (l: string) => /(deg|min|sec|direction)/i.test(l)
const normForDms = (s: string) => String(s || '').toLowerCase().replace(/postion/g, 'position')

/** Multi-row consumption values are stored as JSON arrays; render like the view. */
const renderStoredValueHtml = (rawValue: any, field: any): string => {
  if (isConsumptionMultiRowFieldLabel(field?.label)) {
    const parsed = parseJsonLike(rawValue)
    const arr = Array.isArray(parsed) ? parsed : []
    if (arr.length === 1 && !Array.isArray(arr[0])) {
      return escapeHtml(String(arr[0] ?? '--'))
    }
    if (arr.length > 0) {
      return arr
        .map((row: any, i: number) => {
          const text = Array.isArray(row)
            ? row.map((item) => formatConsumptionStoredValue(item)).join(', ')
            : formatConsumptionStoredValue(row)
          return `<div>Row ${i + 1}: ${escapeHtml(text)}</div>`
        })
        .join('')
    }
  }

  const scalar = normalizeScalarForDisplay(rawValue, field)
  return escapeHtml(scalar === '' ? '--' : scalar)
}

/**
 * Renders a generic tab exactly as the view does: active fields sorted by
 * order, de-duplicated by label (preferring meaningful values), bunker scalar
 * fields hidden for bunker reports, DMS latitude/longitude merged into single
 * values, and bunker rows/attachments rendered as tables.
 */
export const renderGenericTabBodyHtml = (
  fields: any[],
  valuesById: Record<string | number, any>,
  opts?: {isBunkerReport?: boolean}
): string => {
  const sortedFields = sortFieldsByOrder(
    ((fields || []) as any[]).filter((field: any) => field.isActive)
  )

  // De-duplicate labels, preferring the field that actually has a value.
  const seen = new Map<string, any>()
  sortedFields.forEach((field: any) => {
    const labelKey = normalizeFieldLabel(field.label) || `field-${field.id}`
    const rawValue = valuesById?.[field.id]?.value ?? ''
    const existing = seen.get(labelKey)
    if (!existing) {
      seen.set(labelKey, field)
      return
    }
    const existingValue = valuesById?.[existing.id]?.value ?? ''
    if (hasMeaningfulValue(rawValue) && !hasMeaningfulValue(existingValue)) {
      seen.set(labelKey, field)
    }
  })

  const ecaSelection = (() => {
    if (!valuesById) return ''
    for (const [key, entry] of Object.entries(valuesById)) {
      const label = String(entry?.label || key).toLowerCase()
      if (
        label.includes('eca type') ||
        label.includes('event type') ||
        label.includes('changeover direction') ||
        label.includes('sequence step') ||
        label.includes('eca event')
      ) {
        const val = String(entry?.value ?? entry ?? '').toLowerCase()
        if (val) return val
      }
    }
    return ''
  })()
  const isExitSelected = ecaSelection.includes('exit')
  const isEntrySelected = ecaSelection.includes('entry') || ecaSelection.includes('enter')

  const dedupedFields = Array.from(seen.values()).filter((field: any) => {
    if (opts?.isBunkerReport && HIDDEN_BUNKER_SCALAR_LABELS.has(normalizeFieldLabel(field.label))) {
      return false
    }
    const l = String(field.label || '').toLowerCase().replace(/postion/g, 'position')
    if (l.includes('eca exit')) {
      if (!isExitSelected) return false
    } else if (l.includes('eca entry') || l.includes('eca enrty')) {
      if (isExitSelected && !isEntrySelected) return false
    }
    return true
  })

  const merged = dedupedFields.map((field: any) => ({
    field,
    label: String(field.label ?? ''),
    rawValue: valuesById?.[field.id]?.value ?? '',
  }))

  const bunkerRowsEntry = merged.find((m) => normalizeFieldLabel(m.label) === 'bunker received rows')
  const bunkerAttachmentsEntry = merged.find((m) => {
    const normalized = normalizeFieldLabel(m.label)
    return normalized === 'bunker attachments' || normalized === 'attachment rows'
  })

  // DMS latitude/longitude parts merged into single display values.
  const pickDms = (kind: 'latitude' | 'longitude', which: 'start' | 'stop' | 'generic') =>
    merged.filter((m) => {
      const l = normForDms(m.label)
      if (!(l.includes(kind) && isDmsPart(l))) return false
      if (which === 'start') return l.includes('start')
      if (which === 'stop') return l.includes('stop')
      return !l.includes('start') && !l.includes('stop')
    })

  const formatDms = (parts: {label: string; rawValue: any}[]) => {
    const getVal = (needle: 'deg' | 'min' | 'sec' | 'direction') => {
      const found = parts.find((p) => normForDms(p.label).includes(needle))
      const v = found ? String(found.rawValue ?? '').trim() : ''
      return v || '--'
    }
    const pad2 = (val: string) => {
      if (!val || val === '--') return val
      return val.length === 1 ? '0' + val : val
    }
    const deg = getVal('deg')
    const min = pad2(getVal('min'))
    const sec = pad2(getVal('sec'))
    const dir = getVal('direction')
    return `${deg || '0'}° ${min || '00'}' ${sec || '00'}" ${dir === '--' ? '' : dir}`.trim()
  }

  const gridItems: GridItem[] = []

  const pushDmsItem = (
    kind: 'latitude' | 'longitude',
    labels: {start: string; stop: string; generic: string}
  ) => {
    const start = pickDms(kind, 'start')
    const stop = pickDms(kind, 'stop')
    const generic = pickDms(kind, 'generic')

    if (start.length || generic.length) {
      gridItems.push({
        label: start.length ? labels.start : labels.generic,
        valueHtml: escapeHtml(formatDms(start.length ? start : generic)),
      })
    }
    if (stop.length) {
      gridItems.push({label: labels.stop, valueHtml: escapeHtml(formatDms(stop))})
    }
  }

  pushDmsItem('latitude', {
    start: 'Start Position Latitude',
    stop: 'Stop Position Latitude',
    generic: 'Latitude',
  })
  pushDmsItem('longitude', {
    start: 'Start Position Longitude',
    stop: 'Stop Position Longitude',
    generic: 'Longitude',
  })

  // Remaining scalar fields (exclude DMS parts and bunker JSON payload fields).
  merged.forEach((m) => {
    const l = normForDms(m.label)
    const normalized = normalizeFieldLabel(m.label)
    const isBunkerJsonField =
      normalized === 'bunker received rows' ||
      normalized === 'bunker attachments' ||
      normalized === 'attachment rows'
    if (((l.includes('latitude') || l.includes('longitude')) && isDmsPart(l)) || isBunkerJsonField) {
      return
    }
    gridItems.push({
      label: getCleanLabel(m.label),
      valueHtml: renderStoredValueHtml(m.rawValue, m.field),
    })
  })

  let out = renderFieldGridHtml(gridItems)

  if (bunkerRowsEntry) out += renderBunkerRowsTableHtml(bunkerRowsEntry.rawValue)
  if (bunkerAttachmentsEntry) out += renderBunkerAttachmentsTableHtml(bunkerAttachmentsEntry.rawValue)

  return out
}

// ---------------------------------------------------------------------------
// Miscellaneous Consumables: Sr. No | Parameter | Value table (mirrors the view)
// ---------------------------------------------------------------------------
export const renderMiscConsumablesTableHtml = (mergedFields: [string, any][]): string => {
  if (!mergedFields.length) return mutedNoteHtml('No data captured for this section.')

  let out = `
    <table style="${TABLE_STYLE}">
      <thead>
        <tr>
          <th style="${TH_STYLE} width:50px; text-align:center;">Sr. No</th>
          <th style="${TH_STYLE}">Parameter</th>
          <th style="${TH_STYLE} width:30%;">Value</th>
        </tr>
      </thead>
      <tbody>
  `

  mergedFields.forEach(([label, value], idx) => {
    const display = normalizeScalarForDisplay(value, {label}) || '--'
    const zebra = idx % 2 === 1 ? ` background:${ZEBRA_BG};` : ''
    out += `
      <tr style="page-break-inside:avoid;${zebra}">
        <td style="${TD_STYLE} text-align:center;">${idx + 1}</td>
        <td style="${TD_STYLE}">${escapeHtml(getCleanLabel(label))}</td>
        <td style="${TD_STYLE} font-weight:600;">${escapeHtml(display)}</td>
      </tr>
    `
  })

  out += '</tbody></table>'
  return out
}

// ---------------------------------------------------------------------------
// Consumption & ROB: fuel blocks + Lube Oil Status table (mirrors the view)
// ---------------------------------------------------------------------------

/** Machinery names that expose an Operation Type in the view. */
const showsOperationType = (machinery: any): boolean => {
  const machineKey = String(machinery ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '')
  return (
    machineKey === 'boiler1' || machineKey === 'boiler2' || machineKey === 'ae1' || machineKey === 'ae2'
  )
}

export const renderConsumptionRobSectionHtml = (
  consumptionData: any,
  opts?: { isBunkerReport?: boolean; isFuelChangeoverReport?: boolean }
): string => {
  if (!consumptionData) {
    return mutedNoteHtml('No consumption data available for this report.')
  }

  let out = ''

  if (consumptionData?.remarks) {
    out += `
      <div style="border:1px solid ${BORDER}; border-left:4px solid ${ACCENT}; border-radius:4px; background:${ZEBRA_BG}; padding:8px 12px; margin-bottom:12px; page-break-inside:avoid;">
        <div style="${LABEL_STYLE}">Remarks</div>
        <div style="${VALUE_STYLE}">${escapeHtml(consumptionData.remarks)}</div>
      </div>
    `
  }

  const isChangeover = Boolean(
    opts?.isFuelChangeoverReport ||
      (Array.isArray(consumptionData?.tanks) && consumptionData.tanks.length > 0)
  )

  if (isChangeover) {
    const tanks: any[] =
      Array.isArray(consumptionData?.tanks) && consumptionData.tanks.length > 0
        ? consumptionData.tanks
        : synthesizeTankWiseFuelRows(consumptionData?.rows || [])
    let totalVlsfo = 0
    let totalLsmgo = 0
    let totalConsumed = 0
    let activeServiceTankSulphur = ''
    let isEcaCompliant = false
    const hasData = tanks.length > 0

    tanks.forEach((t) => {
      const cur = Number(t.currentRob) || 0
      const cons = Number(t.consumedTransferred) || 0
      totalConsumed += cons
      const isLsmgo =
        t.fuelType === 'LSMGO' ||
        t.fuelType === 'MGO' ||
        t.fuelType === 'ULSFO' ||
        String(t.q88Grade).includes('0.1%') ||
        String(t.tankName).toUpperCase().includes('LSMGO') ||
        (parseFloat(t.sulphurPercentage) <= 0.10 && parseFloat(t.sulphurPercentage) > 0)

      if (isLsmgo) {
        totalLsmgo += cur
      } else {
        totalVlsfo += cur
      }

      if (
        String(t.allocationStatus).toLowerCase().includes('active') ||
        String(t.allocationStatus).toLowerCase().includes('in service') ||
        String(t.tankName).toLowerCase().includes('service')
      ) {
        activeServiceTankSulphur = t.sulphurPercentage
        const sNum = parseFloat(t.sulphurPercentage)
        if (Number.isFinite(sNum) && sNum <= 0.10) {
          isEcaCompliant = true
        }
      }
    })

    const resolvedActiveSulphur =
      activeServiceTankSulphur ||
      (totalLsmgo > 0 ? '0.09%' : totalVlsfo > 0 ? '0.48%' : '--')

    // 4 KPI Summary Cards for PDF/HTML Export
    out += `
      <div style="margin-bottom:14px; page-break-inside:avoid;">
        <table style="width:100%; border-collapse:collapse; margin-bottom:12px;">
          <tr>
            <td style="width:25%; padding:8px; border:1px solid ${BORDER}; background:#f0f6ff; vertical-align:top;">
              <div style="${LABEL_STYLE}">Total VLSFO ROB</div>
              <div style="font-size:16px; font-weight:700; color:${NAVY};">${totalVlsfo.toFixed(2)} MT</div>
              <div style="font-size:8px; color:${MUTED};">Pre-changeover HFO/VLSFO</div>
            </td>
            <td style="width:25%; padding:8px; border:1px solid ${BORDER}; background:#f0fdf4; vertical-align:top;">
              <div style="${LABEL_STYLE}">Total LSMGO ROB (ECA)</div>
              <div style="font-size:16px; font-weight:700; color:#16a34a;">${totalLsmgo.toFixed(2)} MT</div>
              <div style="font-size:8px; color:${MUTED};">ECA Compliant Fuel</div>
            </td>
            <td style="width:25%; padding:8px; border:1px solid ${BORDER}; background:#f5f3ff; vertical-align:top;">
              <div style="${LABEL_STYLE}">Total Consumed / Transferred</div>
              <div style="font-size:16px; font-weight:700; color:#2563eb;">${totalConsumed.toFixed(2)} MT</div>
              <div style="font-size:8px; color:${MUTED};">Voyage Flushing &amp; Use</div>
            </td>
            <td style="width:25%; padding:8px; border:1px solid ${BORDER}; background:${isEcaCompliant ? '#f0fdf4' : '#fffbeb'}; vertical-align:top;">
              <div style="${LABEL_STYLE}">ECA Changeover Status</div>
              <div style="font-size:11px; font-weight:700; color:${isEcaCompliant ? '#16a34a' : '#d97706'}; margin-top:2px;">
                ${isEcaCompliant ? 'COMPLIANT (&le; 0.10% S)' : hasData ? `PRE-CHANGEOVER (${resolvedActiveSulphur})` : 'PENDING TELEMETRY'}
              </div>
              <div style="font-size:8px; color:${MUTED};">
                ${hasData ? `Active Service Tank ${resolvedActiveSulphur}` : 'No position reports submitted'}
              </div>
            </td>
          </tr>
        </table>
      </div>
    `

    // 9-column Tank-Wise Table
    if (tanks.length > 0) {
      out += subheadingHtml('Tank-Wise Fuel Allocation & Q88 Grades')
      out += `
        <table style="${TABLE_STYLE} margin-bottom:14px;">
          <thead>
            <tr>
              <th style="${TH_STYLE} width:30px; text-align:center;">#</th>
              <th style="${TH_STYLE}">Tank Name</th>
              <th style="${TH_STYLE}">Q88 Grade</th>
              <th style="${TH_STYLE}">Fuel</th>
              <th style="${TH_STYLE} text-align:right;">Sounding (MT)</th>
              <th style="${TH_STYLE} text-align:right;">Consumed (MT)</th>
              <th style="${TH_STYLE} text-align:right;">Current ROB</th>
              <th style="${TH_STYLE}">Sulphur %</th>
              <th style="${TH_STYLE}">Allocation Status</th>
            </tr>
          </thead>
          <tbody>
      `
      tanks.forEach((t, tIdx) => {
        const zebra = tIdx % 2 === 1 ? ` background:${ZEBRA_BG};` : ''
        out += `
          <tr style="page-break-inside:avoid;${zebra}">
            <td style="${TD_STYLE} text-align:center;">${tIdx + 1}</td>
            <td style="${TD_STYLE} font-weight:600;">${escapeHtml(t.tankName)}</td>
            <td style="${TD_STYLE} color:#0284c7; font-weight:600;">${escapeHtml(t.q88Grade)}</td>
            <td style="${TD_STYLE}">${escapeHtml(t.fuelType)}</td>
            <td style="${TD_STYLE} text-align:right;">${Number(t.soundingRob).toFixed(2)}</td>
            <td style="${TD_STYLE} text-align:right; color:#dc2626;">${Number(t.consumedTransferred) > 0 ? Number(t.consumedTransferred).toFixed(2) : '0.00'}</td>
            <td style="${TD_STYLE} text-align:right; font-weight:700; color:#16a34a;">${Number(t.currentRob).toFixed(2)}</td>
            <td style="${TD_STYLE}">${escapeHtml(t.sulphurPercentage)}</td>
            <td style="${TD_STYLE}">${escapeHtml(t.allocationStatus)}</td>
          </tr>
        `
      })
      out += `</tbody></table>`
    }
  } else {
    // Standard position report fuel blocks
    const rows = Array.isArray(consumptionData?.rows) ? consumptionData.rows : []

    if (rows.length === 0) {
      out += mutedNoteHtml('No consumption telemetry available for this report.')
    }

    rows.forEach((r: any) => {
      const machineries = Array.isArray(r?.machineries) ? r.machineries : []
      const totalConsumed = machineries.reduce((sum: number, m: any) => {
        const n = Number(m?.consumed)
        return sum + (Number.isFinite(n) ? n : 0)
      }, 0)

      out += `
        <div style="border:1px solid ${BORDER}; border-radius:6px; margin-bottom:12px; page-break-inside:avoid; overflow:hidden;">
          <div style="background:${SECTION_BG}; border-bottom:1px solid ${BORDER}; padding:6px 12px; display:flex; justify-content:space-between; align-items:center;">
            <div>
              ${
                r?.tankName
                  ? `<span style="${LABEL_STYLE} display:inline; margin-right:4px;">Tank</span><strong style="margin-right:10px; color:${NAVY};">${escapeHtml(
                      r.tankName
                    )}</strong>`
                  : ''
              }
              ${
                r?.q88Grade
                  ? `<span style="${LABEL_STYLE} display:inline; margin-right:4px;">Q88</span><strong style="margin-right:10px; color:#0284c7;">${escapeHtml(
                      r.q88Grade
                    )}</strong>`
                  : ''
              }
              <span style="${LABEL_STYLE} display:inline; margin-right:4px;">Fuel Type</span>
              <strong style="color:${NAVY};">${escapeHtml(r?.fieldType || r?.fuelType)}</strong>
              ${
                r?.sulphurPercentage
                  ? `<span style="margin-left:8px; font-size:9px; font-weight:600; color:${MUTED};">(${escapeHtml(
                      r.sulphurPercentage
                    )})</span>`
                  : ''
              }
            </div>
            <div>
              <span style="${LABEL_STYLE} display:inline; margin-right:6px;">Opening ROB</span>
              <span style="${VALUE_STYLE} display:inline; margin-right:14px;">${escapeHtml(
        formatConsumptionMetric(r?.initialRob)
      )} MT</span>
              <span style="${LABEL_STYLE} display:inline; margin-right:6px;">Current ROB</span>
              <span style="${VALUE_STYLE} display:inline;">${escapeHtml(
        formatConsumptionMetric(r?.lastRob ?? r?.rob)
      )} MT</span>
            </div>
          </div>
          <div style="padding:10px 12px;">
      `

      if (machineries.length === 0) {
        out += mutedNoteHtml('No machinery data')
      } else {
        out += `
          <table style="${TABLE_STYLE}">
            <thead>
              <tr>
                <th style="${TH_STYLE} width:36px; text-align:center;">#</th>
                <th style="${TH_STYLE}">Machinery</th>
                <th style="${TH_STYLE} text-align:right;">Consumed (MT)</th>
                <th style="${TH_STYLE} text-align:right;">ROB (MT)</th>
                <th style="${TH_STYLE}">Operation Type</th>
              </tr>
            </thead>
            <tbody>
        `
        machineries.forEach((mach: any, machIndex: number) => {
          const zebra = machIndex % 2 === 1 ? ` background:${ZEBRA_BG};` : ''
          const machName = mach?.machinery || mach?.value || '--'
          out += `
            <tr style="page-break-inside:avoid;${zebra}">
              <td style="${TD_STYLE} text-align:center;">${machIndex + 1}</td>
              <td style="${TD_STYLE}">${escapeHtml(machName)}</td>
              <td style="${TD_STYLE} text-align:right;">${escapeHtml(
            formatConsumptionMetric(mach?.consumed)
          )}</td>
              <td style="${TD_STYLE} text-align:right;">${escapeHtml(
            formatConsumptionMetric(mach?.rob)
          )}</td>
              <td style="${TD_STYLE}">${escapeHtml(
            showsOperationType(machName) ? mach?.operationType || '--' : '--'
          )}</td>
            </tr>
          `
        })
        out += `
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2" style="${TD_STYLE} background:${SECTION_BG}; font-weight:700; text-align:right;">Total Consumption</td>
                <td style="${TD_STYLE} background:${SECTION_BG}; font-weight:700; text-align:right;">${escapeHtml(
          formatConsumptionMetric(totalConsumed)
        )} MT</td>
                <td colspan="2" style="${TD_STYLE} background:${SECTION_BG};"></td>
              </tr>
            </tfoot>
          </table>
        `
      }

      out += `
          </div>
        </div>
      `
    })
  }

  const lubeOils = Array.isArray(consumptionData?.lubeOils) ? consumptionData.lubeOils : []

  if (lubeOils.length > 0) {
    out += subheadingHtml('Lube Oil Status')
    out += `
      <table style="${TABLE_STYLE}">
        <thead>
          <tr>
            <th style="${TH_STYLE} width:50px; text-align:center;">Sr. No</th>
            <th style="${TH_STYLE}">Grades</th>
            <th style="${TH_STYLE} text-align:right;">Opening Stock</th>
            <th style="${TH_STYLE} text-align:right;">Received</th>
            <th style="${TH_STYLE} text-align:right;">Consumed by Vessel</th>
            <th style="${TH_STYLE} text-align:right;">Closing Stock</th>
          </tr>
        </thead>
        <tbody>
    `
    lubeOils.forEach((lube: any, idx: number) => {
      const zebra = idx % 2 === 1 ? ` background:${ZEBRA_BG};` : ''
      out += `
        <tr style="page-break-inside:avoid;${zebra}">
          <td style="${TD_STYLE} text-align:center;">${idx + 1}</td>
          <td style="${TD_STYLE}">${escapeHtml(lube?.grade)}</td>
          <td style="${TD_STYLE} text-align:right;">${escapeHtml(
        formatConsumptionMetric(lube?.openingStock)
      )}</td>
          <td style="${TD_STYLE} text-align:right;">${escapeHtml(
        formatConsumptionMetric(lube?.received)
      )}</td>
          <td style="${TD_STYLE} text-align:right;">${escapeHtml(
        formatConsumptionMetric(lube?.consumedByVessel)
      )}</td>
          <td style="${TD_STYLE} text-align:right;">${escapeHtml(
        formatConsumptionMetric(lube?.closingStock)
      )}</td>
        </tr>
      `
    })
    out += '</tbody></table>'
  }

  return out
}
